import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';
import { TemplateEntry } from '../template-entry';
import * as Styled from './styles';
import * as SX from './sx';
import type { TemplateEntriesProps as Props } from './types';
import { useNextEntryLabel } from './use-next-entry-label';

export const TemplateEntries = memo(
  ({
    className,
    entries,
    onToggle,
    onSelectAll,
    onDeselectAll,
    onToggleEntryChecked,
    onActiveEntry,
    onAddEntry,
    onRemove,
    selectedOptionsCountInEntry,
    isSingle,
    checkedValues,
  }: Props) => {
    // State to track which entry is currently expanded (accordion behavior)
    const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
    const hasSetDefaultEntry = useRef(false);
    const previousEntriesLength = useRef(0);

    // Set first entry as expanded by default when entries are loaded
    useEffect(() => {
      const firstEntry = entries.filter((i) => !i.isBottom)[0];
      if (firstEntry && !hasSetDefaultEntry.current) {
        setExpandedEntry(firstEntry.name);
        hasSetDefaultEntry.current = true;
      }
    }, [entries]);

    // Auto-expand newly added entry
    useEffect(() => {
      const currentEntriesLength = entries.filter((i) => !i.isBottom).length;

      // If a new entry was added (length increased)
      if (
        currentEntriesLength > previousEntriesLength.current &&
        currentEntriesLength > 0
      ) {
        const lastEntry = entries.filter((i) => !i.isBottom).slice(-1)[0];
        if (lastEntry) {
          setExpandedEntry(lastEntry.name);
        }
      }

      previousEntriesLength.current = currentEntriesLength;
    }, [entries]);

    const handleEntryToggle = (entryName: string) => {
      setExpandedEntry(expandedEntry === entryName ? null : entryName);
    };

    const nextEntryLabel = useNextEntryLabel(
      entries.filter((i) => !i.isBottom).length
    );

    return (
      <Styled.Wrapper
        className={classnames('TemplateEntries', className)}
        sx={SX.root}
        gap={1.5}
      >
        {entries
          .filter((i) => !i.isBottom)
          .map((item) => (
            <TemplateEntry
              key={item.name}
              isSingle={isSingle}
              entry={item}
              expanded={expandedEntry === item.name}
              checkedValues={checkedValues}
              onToggle={onToggle(item.name)}
              onSelectAll={onSelectAll(item.name)}
              onDeselectAll={onDeselectAll(item.name)}
              onToggleEntryChecked={() => onToggleEntryChecked(item.name)}
              onActiveEntry={() => onActiveEntry(item.name)}
              onRemove={() => onRemove(item.name)}
              onToggleExpanded={() => handleEntryToggle(item.name)}
              selectedOptionsCountInEntry={selectedOptionsCountInEntry(
                item.name
              )}
            />
          ))}
        {!isSingle && (
          <Stack gap={1.5}>
            <Card variant="outlined" sx={SX.card}>
              <CardContent sx={SX.cardContent} padding="extra">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Checkbox checked={false} size="small" disabled={true} />

                    <Typography
                      variant="body3"
                      fontWeight={500}
                      color="grey.700"
                    >
                      {nextEntryLabel}
                    </Typography>
                  </Stack>

                  <Button
                    variant="outlined"
                    size="xsmall"
                    startIcon={<Icon name="plus" />}
                    onClick={onAddEntry}
                  >
                    Add
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {entries
          .filter((i) => i.isBottom)
          .map((item) => (
            <TemplateEntry
              key={item.name}
              entry={item}
              expanded={expandedEntry === item.name}
              checkedValues={checkedValues}
              onToggle={onToggle(item.name)}
              onAddEntry={() => onAddEntry()}
              onSelectAll={onSelectAll(item.name)}
              onDeselectAll={onDeselectAll(item.name)}
              onToggleEntryChecked={() => onToggleEntryChecked(item.name)}
              onActiveEntry={() => onActiveEntry(item.name)}
              onRemove={() => onRemove(item.name)}
              onToggleExpanded={() => handleEntryToggle(item.name)}
              selectedOptionsCountInEntry={selectedOptionsCountInEntry(
                item.name
              )}
            />
          ))}
      </Styled.Wrapper>
    );
  }
);

TemplateEntries.displayName = 'TemplateEntries';
